import { type FC } from "react";
import type { PlainDailyUsage } from "@/queries/ona-intelligence-queries";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/Chart";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card } from "@/components/podkit/Card";
import { Text } from "@/components/podkit/typography/Text";
import { SECONDARY_AXIS_COLOR } from "@/components/insights/insights-constants";

type Props = {
    dailyBreakdown: PlainDailyUsage[];
    animationKey?: string;
};

const chartConfig = {
    credits: {
        label: "Credits",
        color: SECONDARY_AXIS_COLOR,
    },
} satisfies ChartConfig;

export const UsageChart: FC<Props> = ({ dailyBreakdown, animationKey }) => {
    const chartData = dailyBreakdown.map((day) => {
        const date = day.date ? new Date(Number(day.date.seconds) * 1000) : new Date();
        return {
            date: date.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
            }),
            credits: day.creditsConsumed ?? 0,
        };
    });

    return (
        <Card className="p-6">
            <div className="mb-4">
                <Text className="text-lg font-semibold text-content-primary">Daily Credit Usage</Text>
                <Text className="text-sm text-content-secondary">Credits consumed per day</Text>
            </div>

            <ChartContainer config={chartConfig} className="h-[300px] w-full">
                <AreaChart key={animationKey} data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id="fillCredits" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--color-credits)" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="var(--color-credits)" stopOpacity={0.06} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        minTickGap={32}
                        tickFormatter={(value: string) => value}
                    />
                    <YAxis
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        tickFormatter={(value: number) =>
                            value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toString()
                        }
                    />
                    <ChartTooltip
                        content={
                            <ChartTooltipContent
                                valueFormatter={(value) =>
                                    `${Number(value).toLocaleString(undefined, {
                                        minimumFractionDigits: 1,
                                        maximumFractionDigits: 1,
                                    })} credits`
                                }
                            />
                        }
                    />
                    <Area
                        type="monotone"
                        dataKey="credits"
                        stroke="var(--color-credits)"
                        fill="url(#fillCredits)"
                        strokeWidth={2}
                        dot={false}
                        isAnimationActive={true}
                        animationDuration={1800}
                        animationEasing="ease-in-out"
                    />
                </AreaChart>
            </ChartContainer>
        </Card>
    );
};
